import * as THREE from 'three';
import { hashUnit } from '../../shared/rng.js';
import type { FacadeStyle } from '../../shared/types.js';

/**
 * Building walls, drawn in the shader: windows, frames, sills, balconies, cornices, brick,
 * the rooms behind the glass and the glass itself. DECISIONS.md §81.
 *
 * It replaced six hand-drawn canvas tiles, one per `FacadeStyle`, and the reason is the
 * thing a player noticed first: every building of an era was the same building. A tile is
 * a picture of one facade, and whatever varies has to be baked into a different picture.
 * Here a facade is a handful of numbers — how wide the windows are, whether they have
 * shutters, whether there is a balcony and on which floors, what colour the frames are
 * painted — and each building draws its own from a hash of its id, so two tenements side by
 * side are two tenements. It costs no triangles: a wall is still two per bay-run, exactly as
 * before, and all of this is arithmetic on the UVs the extruder already wrote.
 *
 * **Glass reads as glass.** It was a pale grey multiplier on the wall colour. Now it is
 * dark, smooth and reflective, so the environment map puts the sky in it — brighter at a
 * grazing angle, a hard glint where the sun is — and where the reflected ray would hit the
 * building across the street rather than the sky, it reflects that instead, darker. Behind
 * the glass is a room: a box a bay wide, a storey high and four metres deep, ray-traced in
 * the fragment shader ("interior mapping") with a floor, a ceiling, walls of its own colour,
 * a piece of furniture, and curtains, blinds or nothing in front of it. At night some of
 * those rooms have a light on. That replaced the lit-window texture, which repeated every
 * eight bays and eight floors; a hash per window does not repeat at all.
 *
 * **Nothing shimmers.** Every edge here is filtered over the pixel it lands in — a window is
 * the fraction of the pixel its rectangle covers, a mortar line the fraction its stripe
 * covers — and once a whole bay is only a few pixels across the detail fades to the average
 * it would have come to anyway. That is what lets the windows be properly dark against the
 * wall, where the old tiles had to be washed out so that a distant terrace would not crawl.
 */

/**
 * How wide a bay is, by era, in metres — and a building draws its own from the range. A
 * fixed 3.6 m put every window in the city on the same grid, which a street of different
 * buildings never is; a tenement's windows are not a curtain wall's mullions.
 */
const BAY_RANGE: Record<FacadeStyle, [number, number]> = {
  period: [3.0, 4.1],
  ornate: [3.2, 4.2],
  modern: [3.0, 4.2],
  curtain: [1.6, 2.6],
  utility: [4.2, 6.5],
  vernacular: [2.8, 3.9],
};

/** The building's own draw, 0–1: everything the facade varies is hashed from it. Its own axis, so
 * the facade's details are not welded to the palette pick in `classify.ts`. */
export function facadeSeedFor(worldSeed: number, buildingId: number): number {
  return hashUnit(worldSeed, buildingId ^ 0x51ed27);
}

export function bayMetresFor(style: FacadeStyle, seed: number): number {
  const [narrow, wide] = BAY_RANGE[style];
  return narrow + (wide - narrow) * seed;
}

/** Packed into `facadeA.x` above the style: this building has a shopfront band, and this quad is it. */
export const FACADE_FLAG_BANDED = 1;
export const FACADE_FLAG_BAND = 2;

const GLSL_PARS = /* glsl */ `
uniform float facadeNight;
uniform float facadeDay;
varying vec4 vFacadeA;   // style + 8 * flags, material, seed, bays on this wall
varying vec4 vFacadeB;   // wall bottom Y, wall top Y, bay metres, storey metres
varying vec2 vFacadeUv;  // bays along, storeys up
varying vec3 vFacadeWorld;
varying vec3 vFacadeN;

struct FacadeOut {
	vec3 albedo;
	float rough;
	float metal;
	vec3 emit;
	float glass;
	float reflOcc;
};

float fHash( vec2 p ) {
	vec3 p3 = fract( vec3( p.xyx ) * 0.1031 );
	p3 += dot( p3, p3.yzx + 33.33 );
	return fract( ( p3.x + p3.y ) * p3.z );
}
float fHash1( float x ) { return fHash( vec2( x, x * 1.618 + 7.13 ) ); }

float fNoise( vec2 p ) {
	vec2 i = floor( p );
	vec2 f = fract( p );
	f = f * f * ( 3.0 - 2.0 * f );
	return mix( mix( fHash( i ), fHash( i + vec2( 1.0, 0.0 ) ), f.x ),
		mix( fHash( i + vec2( 0.0, 1.0 ) ), fHash( i + vec2( 1.0, 1.0 ) ), f.x ), f.y );
}

// How much of [p - w, p + w] lies inside [lo, hi]: an exact box filter of one interval.
float fBand( float p, float lo, float hi, float w ) {
	return clamp( ( min( p + w, hi ) - max( p - w, lo ) ) / ( 2.0 * w ), 0.0, 1.0 );
}
float fRect( vec2 p, vec2 lo, vec2 hi, vec2 w ) {
	return fBand( p.x, lo.x, hi.x, w.x ) * fBand( p.y, lo.y, hi.y, w.y );
}
// The same for a stripe [a, b] repeating every 1: the integral of the pulse, differenced.
float fPulseI( float t, float a, float b ) { return floor( t ) * ( b - a ) + clamp( fract( t ), a, b ) - a; }
float fPulse( float x, float a, float b, float w ) {
	return ( fPulseI( x + w, a, b ) - fPulseI( x - w, a, b ) ) / ( 2.0 * w );
}

vec3 fPick4( float h, vec3 a, vec3 b, vec3 c, vec3 d ) {
	return h < 0.25 ? a : h < 0.5 ? b : h < 0.75 ? c : d;
}

// A room behind a window, seen through a point on the glass. Interior mapping: the ray from
// the eye is carried on into a box a bay wide, a storey tall and ROOM_DEPTH deep, and
// whichever face it meets first is what is drawn there. d is the view direction in the
// wall's frame — x along it, y up, z into it.
vec3 facadeRoom( vec2 at, vec3 d, vec2 size, float rid, float lit, float shop ) {
	const float ROOM_DEPTH = 4.2;
	float W = size.x;
	float H = size.y;
	float tx = d.x > 0.0 ? ( W - at.x ) / d.x : -at.x / min( d.x, -1e-4 );
	float ty = d.y > 0.0 ? ( H - at.y ) / d.y : -at.y / min( d.y, -1e-4 );
	float tz = ROOM_DEPTH / max( d.z, 1e-4 );
	float t = min( min( tx, ty ), tz );
	vec3 h = vec3( at, 0.0 ) + d * t;

	vec3 wallCol = fPick4( fHash1( rid * 3.7 ), vec3( 0.82, 0.78, 0.68 ), vec3( 0.72, 0.77, 0.80 ),
		vec3( 0.76, 0.80, 0.70 ), vec3( 0.84, 0.82, 0.80 ) );
	vec3 col;
	if ( t == tz ) {
		col = wallCol;
		// Something against the back wall: a sofa, a dresser, a shelf. One block, a third of the time
		// two, and only below a metre: it is what turns a box into a room.
		float fx = fHash1( rid * 5.3 );
		float fw = 0.5 + fHash1( rid * 2.9 ) * 0.9;
		float furniture = step( abs( h.x - W * ( 0.25 + 0.5 * fx ) ), fw ) * step( h.y, 0.9 + 0.8 * step( 0.7, fx ) );
		col = mix( col, fPick4( fHash1( rid * 8.1 ), vec3( 0.30, 0.22, 0.16 ), vec3( 0.22, 0.26, 0.34 ),
			vec3( 0.55, 0.50, 0.44 ), vec3( 0.34, 0.18, 0.16 ) ), furniture );
		// A picture, or shelves in a shop.
		float picture = step( abs( h.x - W * 0.5 ), 0.45 ) * step( abs( h.y - 1.75 ), 0.3 ) * step( 0.45, fHash1( rid * 4.4 ) );
		col = mix( col, vec3( 0.25, 0.30, 0.32 ), picture * ( 1.0 - furniture ) );
		// A shop's back wall is shelving: rows of goods in whatever colours the shop sells.
		float shelf = floor( h.y * 2.2 );
		vec3 goods = fPick4( fHash( vec2( floor( h.x * 3.0 ), shelf + rid * 9.0 ) ), vec3( 0.7, 0.3, 0.25 ),
			vec3( 0.25, 0.4, 0.6 ), vec3( 0.85, 0.8, 0.6 ), vec3( 0.3, 0.5, 0.3 ) );
		col = mix( col, goods * ( 0.55 + 0.45 * step( 0.25, fract( h.y * 2.2 ) ) ), shop * step( 0.4, h.y ) );
	} else if ( t == ty ) {
		col = d.y < 0.0
			? mix( vec3( 0.36, 0.25, 0.16 ), vec3( 0.46, 0.34, 0.22 ), fHash( vec2( floor( h.x * 6.0 ), rid ) ) )
			: vec3( 0.9 );
	} else {
		col = wallCol * 0.86;
	}
	// The corners and the back of the room are darker than the middle of it.
	float ex = min( h.x, W - h.x );
	float ey = min( h.y, H - h.y );
	float ez = ROOM_DEPTH - h.z;
	float corner = min( min( ex + ey, ex + ez ), ey + ez );
	col *= mix( 0.55, 1.0, smoothstep( 0.0, 0.7, corner ) );

	// By day the room is lit from the window and falls off towards the back — and is a great
	// deal dimmer than the street, which is why a window looks dark from outside. A shop has
	// its lights on all day. At night, a lamp in the middle of the ceiling, warm or — one in
	// five — the cold white of a newer bulb.
	float daylight = facadeDay * ( mix( 0.06, 0.015, h.z / ROOM_DEPTH ) + shop * 0.05 );
	vec3 bulb = mix( vec3( 1.0, 0.72, 0.42 ), vec3( 0.86, 0.9, 1.0 ), step( 0.8, fHash1( rid * 6.6 ) ) );
	vec3 lamp = vec3( W * 0.5, H - 0.2, ROOM_DEPTH * 0.45 ) - h;
	// Bright under the lamp and falling off fast, so a lit room has a hot spot and dark corners
	// rather than being a white box — which is also what the bloom picks out.
	float lampLight = 1.6 / ( 1.0 + dot( lamp, lamp ) * 0.35 ) + 0.18;
	return col * ( daylight + lit * lampLight * bulb * ( 0.6 + 0.8 * fHash1( rid * 9.2 ) ) );
}

FacadeOut facade() {
	FacadeOut o;
	float flags = floor( vFacadeA.x / 8.0 );
	float style = vFacadeA.x - flags * 8.0;
	float matId = vFacadeA.y;
	float seed = vFacadeA.z;
	float wallBays = vFacadeA.w;
	bool banded = mod( flags, 2.0 ) > 0.5;
	bool band = flags > 1.5;
	float bayM = vFacadeB.z;
	float floorM = vFacadeB.w;
	float above = vFacadeWorld.y - vFacadeB.x;
	float belowTop = vFacadeB.y - vFacadeWorld.y;

	vec2 uv = vFacadeUv;
	vec2 cell = fract( uv );
	vec2 id = floor( uv );
	// Half the pixel's footprint, in cell units, and how many cells one pixel spans.
	vec2 fw = max( fwidth( uv ), vec2( 1e-4 ) );
	vec2 hw = fw * 0.5;
	float detail = 1.0 - smoothstep( 0.12, 0.35, max( fw.x, fw.y ) );
	// Metres along and up the whole building, independent of how the wall was split into quads.
	vec2 m = vec2( uv.x * bayM, above );
	vec2 mw = max( fwidth( m ), vec2( 1e-4 ) ) * 0.5;

	vec3 wall = vColor.rgb;
	vec3 albedo = wall;

	// --- per-building parameters --------------------------------------------------------
	float s1 = fHash1( seed * 17.0 + 1.0 );
	float s2 = fHash1( seed * 17.0 + 2.0 );
	float s3 = fHash1( seed * 17.0 + 3.0 );
	float s4 = fHash1( seed * 17.0 + 4.0 );
	float s5 = fHash1( seed * 17.0 + 5.0 );
	float s6 = fHash1( seed * 17.0 + 6.0 );
	float s7 = fHash1( seed * 17.0 + 7.0 );
	float s8 = fHash1( seed * 17.0 + 8.0 );

	float winW = 0.44, winH = 0.52, winY = 0.26;
	float barsV = 1.0, barsH = 0.0, transom = 0.66;
	float sill = 1.0, hood = 0.0, shutters = 0.0, balcony = 0.0, arched = 0.0, paired = 0.0;
	float string = 0.0, quoins = 0.0, rustic = 0.0, spandrel = 0.0, mullions = 0.0, gap = 0.0;
	float cornice = 0.45, plinth = 0.6;
	float glassRough = 0.1, glassMetal = 0.0, litFrac = 0.34;
	vec3 frame = fPick4( s7, vec3( 0.86, 0.84, 0.80 ), vec3( 0.80, 0.77, 0.70 ), vec3( 0.24, 0.17, 0.12 ), vec3( 0.18, 0.24, 0.18 ) );
	vec3 shutterCol = fPick4( s8, vec3( 0.20, 0.32, 0.22 ), vec3( 0.36, 0.25, 0.16 ), vec3( 0.25, 0.32, 0.42 ), vec3( 0.42, 0.42, 0.40 ) );

	if ( style < 0.5 ) { // period
		winW = 0.38 + 0.12 * s1; winH = 0.48 + 0.12 * s2;
		barsH = step( 0.4, s3 ); hood = step( 0.55, s4 ); arched = step( 0.84, s5 ); paired = step( 0.9, s6 );
		string = step( 0.3, s2 ); quoins = step( 0.62, s1 ); rustic = step( 0.7, s3 );
		cornice = 0.4 + 0.45 * s4; plinth = 0.5 + 0.5 * s5;
	} else if ( style < 1.5 ) { // ornate
		winW = 0.36 + 0.08 * s1; winH = 0.58 + 0.1 * s2; winY = 0.2; transom = 0.72;
		barsH = 1.0; hood = 1.0; arched = step( 0.75, s5 );
		balcony = s3 < 0.45 ? 1.0 : 2.0; shutters = step( 0.82, s6 );
		string = 1.0; quoins = step( 0.6, s1 ); rustic = step( 0.3, s4 );
		cornice = 0.75 + 0.5 * s4; plinth = 0.7 + 0.5 * s5;
		frame = mix( frame, vec3( 0.84, 0.82, 0.78 ), step( 0.4, s7 ) );
	} else if ( style < 2.5 ) { // modern
		winW = 0.66 + 0.18 * s1; winH = 0.32 + 0.1 * s2; winY = 0.3;
		barsV = s3 < 0.5 ? 1.0 : 2.0; sill = 0.0; spandrel = 1.0; gap = step( 0.75, s4 );
		cornice = 0.25; plinth = 0.35; litFrac = 0.38;
		frame = fPick4( s7, vec3( 0.20, 0.21, 0.22 ), vec3( 0.55, 0.56, 0.56 ), vec3( 0.85, 0.85, 0.83 ), vec3( 0.30, 0.24, 0.18 ) );
	} else if ( style < 3.5 ) { // curtain
		winW = 0.94; winH = 0.7 + 0.08 * s2; winY = 0.18;
		barsV = 2.0; sill = 0.0; spandrel = 1.0; mullions = 1.0;
		cornice = 0.3; plinth = 0.0; litFrac = 0.55; glassRough = 0.05; glassMetal = 0.35;
		frame = mix( vec3( 0.16, 0.17, 0.19 ), vec3( 0.58, 0.6, 0.62 ), step( 0.5, s7 ) );
	} else if ( style < 4.5 ) { // utility
		winW = 0.74; winH = 0.38; winY = 0.44;
		barsV = 3.0; barsH = 2.0; transom = 0.5; sill = 0.0; gap = step( 0.4, s4 );
		cornice = 0.2; plinth = 0.3; litFrac = 0.12; glassRough = 0.22;
		frame = vec3( 0.42, 0.44, 0.45 );
	} else { // vernacular
		winW = 0.28 + 0.08 * s1; winH = 0.34 + 0.08 * s2; winY = 0.3;
		barsH = 1.0; transom = 0.5; shutters = step( 0.45, s6 ); gap = step( 0.6, s4 );
		cornice = 0.18; plinth = 0.45; litFrac = 0.22;
	}

	float floorId = id.y;
	float storeyIdx = band ? -1.0 : floorId + ( banded ? 1.0 : 0.0 );
	bool ground = !band && !banded && floorId < 0.5;
	bool topStorey = belowTop < floorM * 0.98;

	// --- the wall itself ----------------------------------------------------------------
	float grain = 0.0;
	if ( matId < 0.5 ) { // render
		grain = ( fNoise( m * 0.35 + seed * 31.0 ) - 0.5 ) * 0.1 + ( fNoise( m * 2.7 ) - 0.5 ) * 0.05 * detail;
		albedo = wall * ( 1.0 + grain );
	} else if ( matId < 1.5 ) { // brick
		float row = m.y / 0.075;
		float course = floor( row );
		float bx = m.x / 0.25 + 0.5 * mod( course, 2.0 );
		vec2 bw = vec2( mw.x / 0.25, mw.y / 0.075 );
		float mortar = 1.0 - ( 1.0 - fPulse( bx, 0.0, 0.06, max( bw.x, 1e-3 ) ) ) * ( 1.0 - fPulse( row, 0.0, 0.16, max( bw.y, 1e-3 ) ) );
		float tint = fHash( vec2( floor( bx ), course ) + seed * 13.0 );
		vec3 brick = wall * ( 0.82 + 0.34 * tint ) * vec3( 1.0 + 0.08 * ( tint - 0.5 ), 1.0, 1.0 - 0.06 * ( tint - 0.5 ) );
		float resolved = 1.0 - smoothstep( 0.25, 0.6, max( bw.x, bw.y ) );
		albedo = mix( wall * 1.02, mix( brick, vec3( 0.58, 0.55, 0.5 ), mortar ), resolved );
	} else if ( matId < 2.5 ) { // stone
		float row = m.y / 0.42;
		float course = floor( row );
		float bx = m.x / 0.95 + 0.37 * mod( course, 3.0 );
		vec2 bw = vec2( mw.x / 0.95, mw.y / 0.42 );
		float joint = 1.0 - ( 1.0 - fPulse( bx, 0.0, 0.015, max( bw.x, 1e-3 ) ) ) * ( 1.0 - fPulse( row, 0.0, 0.03, max( bw.y, 1e-3 ) ) );
		float tint = fHash( vec2( floor( bx ), course ) + seed * 7.0 );
		albedo = wall * ( 0.94 + 0.12 * tint ) * ( 1.0 - 0.18 * joint * ( 1.0 - smoothstep( 0.3, 0.7, max( bw.x, bw.y ) ) ) );
	} else if ( matId < 3.5 ) { // panel
		float joint = 1.0 - ( 1.0 - fPulse( uv.x, -0.004, 0.004, hw.x ) ) * ( 1.0 - fPulse( uv.y, -0.006, 0.006, hw.y ) );
		float tint = fHash( id + seed * 5.0 );
		albedo = wall * ( 0.95 + 0.08 * tint ) * ( 1.0 - 0.35 * joint * detail );
	} else if ( matId < 4.5 ) { // glass: the spandrels between the floors of a curtain wall
		albedo = wall * 0.75;
	} else { // metal
		float rib = fPulse( m.x / 0.3, 0.0, 0.5, max( mw.x / 0.3, 1e-3 ) );
		albedo = wall * mix( 0.88, 1.04, rib );
	}

	// Splash and grime at the foot of the wall, rain stains under the top.
	albedo *= mix( 0.86, 1.0, smoothstep( 0.0, 1.6, above ) );
	albedo *= 1.0 - 0.07 * smoothstep( 2.5, 0.0, belowTop ) * fNoise( vec2( m.x * 1.7, 0.0 ) );
	float rough = matId > 3.5 && matId < 4.5 ? 0.35 : matId > 4.5 ? 0.5 : 0.88;
	float metal = matId > 4.5 ? 0.25 : 0.0;

	// --- the edges of the building ------------------------------------------------------
	float stoneTrim = 0.0;
	if ( plinth > 0.0 ) {
		float p = fBand( above, -10.0, plinth, mw.y );
		albedo = mix( albedo, wall * 0.7 + vec3( 0.06 ), p );
		stoneTrim = max( stoneTrim, p );
	}
	// The cornice: a lighter band at the top with the shadow of its own overhang under it.
	float c = fBand( belowTop, -1.0, cornice, mw.y );
	float under = fBand( belowTop, cornice, cornice + 0.22, mw.y );
	albedo = mix( albedo, wall * 1.12 + vec3( 0.03 ), c );
	albedo *= 1.0 - 0.45 * under * step( 0.3, cornice );
	if ( quoins > 0.5 ) {
		float edge = min( uv.x, wallBays - uv.x ) * bayM;
		float course = floor( above / 0.5 );
		float width = mod( course, 2.0 ) < 0.5 ? 0.62 : 0.4;
		float q = fBand( edge, -1.0, width, mw.x ) * ( 1.0 - fPulse( above / 0.5, 0.0, 0.06, max( mw.y / 0.5, 1e-3 ) ) );
		albedo = mix( albedo, wall * 1.1 + vec3( 0.03 ), q * ( 1.0 - c ) );
	}
	if ( string > 0.5 && !band ) {
		float sc = fPulse( uv.y, 0.0, 0.05, hw.y );
		albedo = mix( albedo, wall * 1.1 + vec3( 0.02 ), sc * step( 0.5, uv.y ) * ( 1.0 - c ) );
	}
	if ( rustic > 0.5 && ( ground || band ) ) {
		float groove = fPulse( above / 0.45, 0.0, 0.08, max( mw.y / 0.45, 1e-3 ) );
		albedo *= 1.0 - 0.3 * groove * detail;
	}

	// --- the window in this cell --------------------------------------------------------
	float x0 = 0.5 - winW * 0.5;
	float x1 = 0.5 + winW * 0.5;
	float y0 = winY;
	float y1 = winY + winH;
	float rid = fHash( id + seed * 101.0 );
	// Some bays are blank: a vernacular wall is mostly wall, and a shed has a window every few bays.
	float blank = gap * step( fHash( vec2( id.x, seed * 9.0 ) ), 0.45 );
	// One door per wall on the ground floor, at the middle bay.
	bool door = ground && abs( id.x - floor( wallBays * 0.5 ) ) < 0.5 && style != 3.0;
	float shop = 0.0;
	if ( band ) {
		// The shopfront: a display window between piers, a fascia above, a door in one bay.
		x0 = 0.07; x1 = 0.93; y0 = 0.08; y1 = 0.74; barsV = 0.0; barsH = 0.0; sill = 0.0;
		hood = 0.0; arched = 0.0; paired = 0.0; shutters = 0.0; balcony = 0.0; blank = 0.0; shop = 1.0;
		litFrac = 0.6;
		if ( abs( id.x - floor( wallBays * 0.5 ) ) < 0.5 ) { x0 = 0.32; x1 = 0.68; y0 = 0.0; }
		float fascia = fBand( cell.y, 0.78, 0.96, hw.y );
		albedo = mix( albedo, wall * 0.75, fascia );
		float pier = 1.0 - fBand( cell.x, 0.04, 0.96, hw.x );
		albedo = mix( albedo, wall * 1.15, pier * ( 1.0 - fascia ) );
	} else if ( door ) {
		x0 = 0.32; x1 = 0.68; y0 = 0.0; y1 = 0.74; barsV = 0.0; barsH = 0.0; sill = 0.0; arched = 0.0;
		paired = 0.0; shutters = 0.0; blank = 0.0;
	}

	// The opening, and within it the glass: frames and glazing bars are subtracted from it.
	float open = 0.0;
	float wx = 0.0;
	float wy = 0.0;
	if ( paired > 0.5 ) {
		// \`half\` is reserved in GLSL ES 3.0.
		float halfW = winW * 0.22;
		open = max( fRect( cell, vec2( 0.3 - halfW, y0 ), vec2( 0.3 + halfW, y1 ), hw ),
			fRect( cell, vec2( 0.7 - halfW, y0 ), vec2( 0.7 + halfW, y1 ), hw ) );
		wx = fract( ( cell.x - 0.3 + halfW ) / 0.4 );
	} else {
		open = fRect( cell, vec2( x0, y0 ), vec2( x1, y1 ), hw );
		wx = ( cell.x - x0 ) / max( x1 - x0, 1e-3 );
	}
	wy = ( cell.y - y0 ) / max( y1 - y0, 1e-3 );
	if ( arched > 0.5 ) {
		// A round head: the top of the opening is a semicircle, measured in metres so it is round.
		vec2 pm = vec2( ( cell.x - 0.5 ) * bayM, ( cell.y - ( y1 - ( x1 - x0 ) * 0.5 * bayM / floorM ) ) * floorM );
		float r = ( x1 - x0 ) * 0.5 * bayM;
		float inside = clamp( ( r - length( pm ) ) / max( length( mw ) * 1.5, 1e-3 ) + 0.5, 0.0, 1.0 );
		open *= mix( 1.0, inside, step( 0.0, pm.y ) );
	}
	open *= 1.0 - blank;
	if ( mullions > 0.5 ) open = fRect( cell, vec2( 0.0, y0 ), vec2( 1.0, y1 ), hw );

	float frameW = 0.035;
	float glassIn = fRect( cell, vec2( x0 + frameW, y0 + frameW ), vec2( x1 - frameW, y1 - frameW * 0.6 ), hw );
	if ( paired > 0.5 || mullions > 0.5 ) glassIn = open * ( 1.0 - fBand( cell.x, -0.02, 0.012, hw.x ) - fBand( cell.x, 0.988, 1.02, hw.x ) );
	float bars = 0.0;
	if ( barsV > 0.5 ) bars = max( bars, fPulse( wx * ( barsV + 1.0 ) + 0.5, 0.47, 0.53, max( hw.x / max( x1 - x0, 1e-3 ) * ( barsV + 1.0 ), 1e-3 ) ) * step( 0.02, wx ) * step( wx, 0.98 ) );
	if ( barsH > 0.5 ) {
		float tb = barsH > 1.5 ? fPulse( wy * 3.0 + 0.5, 0.46, 0.54, max( hw.y / max( y1 - y0, 1e-3 ) * 3.0, 1e-3 ) ) : fBand( wy, transom - 0.025, transom + 0.025, hw.y / max( y1 - y0, 1e-3 ) );
		bars = max( bars, tb );
	}
	float glass = clamp( glassIn * ( 1.0 - bars ) * ( 1.0 - blank ), 0.0, 1.0 );
	float frameMask = clamp( open - glass, 0.0, 1.0 );

	// Sill and hood: a ledge under the opening, a lintel or pediment over it.
	if ( sill > 0.5 ) {
		float s = fRect( cell, vec2( x0 - 0.04, y0 - 0.045 ), vec2( x1 + 0.04, y0 ), hw ) * ( 1.0 - blank );
		albedo = mix( albedo, wall * 1.15 + vec3( 0.03 ), s );
		// Its shadow on the wall below.
		albedo *= 1.0 - 0.25 * fRect( cell, vec2( x0 - 0.03, y0 - 0.085 ), vec2( x1 + 0.03, y0 - 0.045 ), hw ) * ( 1.0 - blank );
	}
	if ( hood > 0.5 ) {
		float hd = fRect( cell, vec2( x0 - 0.05, y1 + 0.02 ), vec2( x1 + 0.05, y1 + 0.075 ), hw ) * ( 1.0 - blank );
		albedo = mix( albedo, wall * 1.14 + vec3( 0.03 ), hd );
		albedo *= 1.0 - 0.2 * fRect( cell, vec2( x0 - 0.04, y1 ), vec2( x1 + 0.04, y1 + 0.02 ), hw ) * ( 1.0 - blank );
	}
	// The reveal: the opening is set back into the wall, and its upper edge throws a shadow in.
	float reveal = fRect( cell, vec2( x0, y1 - 0.06 ), vec2( x1, y1 ), hw ) * ( 1.0 - blank );
	if ( shutters > 0.5 && !band && !door ) {
		float sw = ( x1 - x0 ) * 0.5;
		float sh = max( fRect( cell, vec2( x0 - sw - 0.01, y0 ), vec2( x0 - 0.01, y1 ), hw ), fRect( cell, vec2( x1 + 0.01, y0 ), vec2( x1 + sw + 0.01, y1 ), hw ) );
		float slats = fPulse( cell.y * 28.0, 0.0, 0.3, max( hw.y * 28.0, 1e-3 ) );
		albedo = mix( albedo, shutterCol * ( 1.0 - 0.25 * slats * detail ), sh * ( 1.0 - blank ) );
	}
	if ( balcony > 0.5 && !band && !ground ) {
		// Ironwork: every floor, or — the Haussmann rhythm — only the second and the fifth.
		float on = balcony < 1.5 ? 1.0 : step( abs( storeyIdx - 2.0 ), 0.5 ) + step( abs( storeyIdx - 5.0 ), 0.5 );
		float continuous = step( 1.5, balcony );
		vec2 lo = vec2( mix( x0 - 0.06, 0.0, continuous ), y0 - 0.2 );
		vec2 hi = vec2( mix( x1 + 0.06, 1.0, continuous ), y0 + 0.02 );
		float rail = fRect( cell, lo, hi, hw ) * on;
		float balusters = max( fPulse( m.x / 0.13, 0.0, 0.3, max( mw.x / 0.13, 1e-3 ) ), fBand( cell.y, hi.y - 0.025, hi.y, hw.y ) + fBand( cell.y, lo.y, lo.y + 0.02, hw.y ) );
		float iron = rail * mix( 0.55, clamp( balusters, 0.0, 1.0 ), detail );
		albedo = mix( albedo, vec3( 0.07, 0.07, 0.075 ), iron );
		// The slab's shadow on the wall under it.
		albedo *= 1.0 - 0.35 * fRect( cell, vec2( lo.x, lo.y - 0.05 ), vec2( hi.x, lo.y ), hw ) * on;
		frameMask *= 1.0 - iron;
		glass *= 1.0 - iron;
	}
	if ( spandrel > 0.5 && !band ) {
		// The panel under each window run, a shade off the wall.
		float sp = fRect( cell, vec2( x0, 0.02 ), vec2( x1, y0 - 0.02 ), hw );
		albedo = mix( albedo, wall * 0.86, sp );
	}

	// Frames are painted, so they keep their colour whatever the wall is.
	albedo = mix( albedo, frame, frameMask );
	albedo *= 1.0 - 0.3 * reveal * ( 1.0 - glass );

	// --- glass --------------------------------------------------------------------------
	vec3 V = normalize( vFacadeWorld - cameraPosition );
	vec3 N = normalize( vFacadeN );
	vec3 T = vec3( -N.z, 0.0, N.x );
	float ndv = clamp( dot( -V, N ), 0.0, 1.0 );
	float fresnel = 0.04 + 0.96 * pow( 1.0 - ndv, 5.0 );

	float lit = step( fHash1( rid * 11.0 + 0.5 ), litFrac ) * facadeNight;
	vec3 room;
	#ifdef FACADE_LOW
		room = vec3( 0.03 * facadeDay ) + lit * vec3( 1.0, 0.72, 0.42 ) * 1.1;
	#else
		vec3 dWall = vec3( dot( V, T ), V.y, dot( V, -N ) );
		vec2 roomSize = vec2( bayM, band ? 4.0 : floorM );
		vec2 at = vec2( cell.x, cell.y ) * roomSize;
		vec3 inside = facadeRoom( at, normalize( dWall ), roomSize, rid, lit, shop );
		// Curtains, blinds or nothing, in front of the room, per window.
		float ct = fHash1( rid * 7.7 );
		// Mostly the colour of net curtains; one in five something with a colour of its own,
		// and muted — a street of lit rooms behind saturated curtains reads as a patchwork quilt.
		float clothRoll = fHash1( rid * 12.3 );
		vec3 cloth = clothRoll < 0.8
			? mix( vec3( 0.88, 0.86, 0.8 ), vec3( 0.78, 0.7, 0.56 ), clothRoll / 0.8 )
			: mix( vec3( 0.5, 0.3, 0.26 ), vec3( 0.34, 0.4, 0.46 ), step( 0.9, clothRoll ) );
		float clothLight = 0.05 * facadeDay + lit * 0.9;
		float drape = 0.0;
		if ( shop < 0.5 ) {
			if ( ct < 0.3 ) drape = step( wx, 0.15 + 0.08 * fHash1( rid ) ) + step( 0.85 - 0.08 * fHash1( rid * 2.0 ), wx );
			else if ( ct < 0.5 ) drape = 0.62;
			else if ( ct < 0.68 ) drape = step( 1.0 - wy, 0.25 + 0.5 * fHash1( rid * 3.0 ) ) * ( 0.75 + 0.25 * step( 0.5, fract( wy * 18.0 ) ) );
			else if ( ct < 0.74 ) drape = 1.0;
		}
		drape = clamp( drape, 0.0, 1.0 );
		vec3 pleat = cloth * ( 0.85 + 0.15 * sin( wx * 70.0 ) );
		room = mix( inside, pleat * clothLight, drape * detail );
		// Far off, a window is one colour: the average of whatever was in it.
		room = mix( vec3( 0.025 * facadeDay ) + lit * vec3( 1.0, 0.72, 0.42 ) * 0.6, room, detail );
	#endif

	// What the glass reflects at a low angle is the street, not the sky: below the skyline
	// across the road the reflection is of a facade, and that is darker. The skyline is taken
	// to be this building's own height, sixteen metres off — a street is usually as tall on
	// both sides as it is anywhere.
	vec3 R = reflect( V, N );
	float skylineTan = max( vFacadeB.y - vFacadeWorld.y, 0.0 ) / 16.0;
	float rTan = R.y / max( length( R.xz ), 1e-3 );
	float street = smoothstep( skylineTan + 0.08, skylineTan - 0.08, rTan );

	o.albedo = mix( albedo, vec3( 0.025, 0.028, 0.032 ), glass );
	o.rough = mix( mix( rough, 0.55, frameMask ), glassRough, glass );
	o.metal = mix( metal, glassMetal, glass );
	o.emit = room * glass * ( 1.0 - fresnel );
	o.glass = glass;
	o.reflOcc = mix( 1.0, 0.18, street );
	return o;
}
`;

export interface FacadeShader {
  /** 0 by day, 1 at full night: how many of the rooms have a light on, and how bright. */
  setNight(amount: number): void;
  setQuality(quality: 'high' | 'low'): void;
}

/**
 * Turns a `MeshStandardMaterial` with vertex colours into the facade. Sets `onBeforeCompile`
 * outright — anything else that patches the walls (the sky field, the street lights) has to
 * chain onto it afterwards, not before.
 */
export function applyFacadeShader(material: THREE.MeshStandardMaterial): FacadeShader {
  const uniforms = {
    facadeNight: { value: 0 },
    facadeDay: { value: 1 },
  };
  let quality: 'high' | 'low' = 'high';
  material.onBeforeCompile = (shader) => {
    Object.assign(shader.uniforms, uniforms);
    shader.vertexShader = shader.vertexShader
      .replace(
        '#include <common>',
        '#include <common>\n' +
          'attribute vec4 facadeA;\nattribute vec4 facadeB;\n' +
          'varying vec4 vFacadeA;\nvarying vec4 vFacadeB;\nvarying vec2 vFacadeUv;\nvarying vec3 vFacadeWorld;\nvarying vec3 vFacadeN;',
      )
      .replace(
        '#include <project_vertex>',
        '#include <project_vertex>\n' +
          '\tvFacadeA = facadeA;\n\tvFacadeB = facadeB;\n\tvFacadeUv = uv;\n' +
          '\tvFacadeWorld = ( modelMatrix * vec4( transformed, 1.0 ) ).xyz;\n' +
          '\tvFacadeN = normalize( mat3( modelMatrix ) * objectNormal );',
      );
    shader.fragmentShader = shader.fragmentShader
      // Just before main rather than at <common>: it reads vColor, declared by then.
      .replace('void main() {', `${GLSL_PARS}\nvoid main() {`)
      .replace('#include <map_fragment>', '\tFacadeOut facadeOut = facade();\n\tdiffuseColor.rgb = facadeOut.albedo;')
      // The vertex colour is the wall's, and `facade()` has already used it.
      .replace('#include <color_fragment>', '')
      .replace('#include <roughnessmap_fragment>', '\tfloat roughnessFactor = facadeOut.rough;')
      .replace('#include <metalnessmap_fragment>', '\tfloat metalnessFactor = facadeOut.metal;')
      .replace('#include <emissivemap_fragment>', '\ttotalEmissiveRadiance += facadeOut.emit;')
      .replace(
        '#include <lights_fragment_end>',
        '#include <lights_fragment_end>\n\treflectedLight.indirectSpecular *= mix( 1.0, facadeOut.reflOcc, facadeOut.glass );',
      );
    if (quality === 'low') shader.fragmentShader = `#define FACADE_LOW\n${shader.fragmentShader}`;
  };
  material.customProgramCacheKey = () => `facade:${quality}`;
  return {
    setNight(amount) {
      uniforms.facadeNight.value = amount;
      uniforms.facadeDay.value = 1 - amount;
    },
    setQuality(next) {
      if (next === quality) return;
      quality = next;
      material.needsUpdate = true;
    },
  };
}
